import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const DEFAULT_FORM = {
  hasRecurrence: "All",
  status: "All",
  isAutopay: "All",
};

const recurrenceStatusOptions = [
  { label: "Active and Inactive", value: "active_inactive" },
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
  { label: "Canceled", value: "Canceled" },
  { label: "Draft", value: "Draft" },
  { label: "Global", value: "Global" },
  { label: "All", value: "All" },
];
const hasRecurrenceOptions = ["Yes", "No", "All"];
const autopayOptions = ["Yes", "No", "All"];

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  onApplyFilters = () => null,
}) {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (opened) {
      setIsClosing(false);
    }
  }, [opened]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    onApplyFilters(formData);
    handleClose();
  };

  const handleClear = () => {
    setFormData(DEFAULT_FORM);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 250);
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        .modal-slide-in { animation: slideInRight 0.3s ease-out forwards; }
        .modal-slide-out { animation: slideOutRight 0.3s ease-in forwards; }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div
          className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
            isClosing ? "modal-slide-out" : "modal-slide-in"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">
                Filters
              </h2>
              <p className="text-xs text-gray-500 font-medium">
                Refine your search results
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700">
            <div className="flex flex-col gap-6">

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Has Recurrence
                </label>
                <select
                  value={formData.hasRecurrence}
                  onChange={(e) => handleChange("hasRecurrence", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  {hasRecurrenceOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Autopay Enabled
                </label>
                <select
                  value={formData.isAutopay}
                  onChange={(e) => handleChange("isAutopay", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  {autopayOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  {recurrenceStatusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

            </div>
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors"
            >
              Clean All Filters
            </button>
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-sky-700 border border-sky-700 rounded-lg hover:bg-sky-800 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
